import { useState } from 'react';
import { formatCurrency } from "./currencyUtils";

const handleChange = (eventOrObject, setFormData, investorType) => {
  if (typeof setFormData !== "function") {
    console.error("setFormData is not a function. Ensure it is passed correctly.");
    return;
  }

  let name, value, type = "text", checked = false;

  if (eventOrObject?.target) {
    ({ name, value, type, checked } = eventOrObject.target);
  } else {
    ({ name, value, type = "text", checked = false } = eventOrObject);
  }

  setFormData(prev => {
    const updatedValue = type === "checkbox" ? checked : value;

    let newData = { ...prev };
    const keys = name.split(".");
    const lastKey = keys.pop();
    let ref = newData;

    for (let i = 0; i < keys.length; i++) {
      const key = keys[i];
      const nextKey = keys[i + 1];

      const isArrayIndex = /^\d+$/.test(nextKey);

      if (!ref[key]) {
        ref[key] = isArrayIndex ? [] : {};
      }

      ref[key] = Array.isArray(ref[key]) ? [...ref[key]] : { ...ref[key] };
      ref = ref[key];

      if (Array.isArray(ref) && /^\d+$/.test(nextKey)) {
        const idx = parseInt(nextKey, 10);
        while (ref.length <= idx) {
          ref.push({});
        }
      }
    }

    if (Array.isArray(ref)) {
      const index = parseInt(lastKey, 10);
      if (!isNaN(index)) {
        ref[index] = updatedValue;
      }
    } else {
      ref[lastKey] = updatedValue;
    }

    if (name === "amount") {
      const raw = Number(updatedValue);
      newData.formattedAmount = formatCurrency(raw);
    }

    if (name === "selectedDate" && value) {
      const [year, month, day] = value.split("-");
      const monthIndex = parseInt(month, 10) - 1;

      const monthNames = [
        "January", "February", "March", "April",
        "May", "June", "July", "August",
        "September", "October", "November", "December"
      ];

      newData.selectedDateDay = day;
      newData.selectedDateMonth = monthNames[monthIndex];
      newData.selectedDateFullYear = year;
      newData.selectedDateYear = year.slice(-1);
    }

    if (name === "selectedDateEntity" && value) {
      const [year, month, day] = value.split("-");
      const monthIndex = parseInt(month, 10) - 1;

      const monthNames = [
        "January", "February", "March", "April",
        "May", "June", "July", "August",
        "September", "October", "November", "December"
      ];

      newData.selectedDateDayEntity = day;
      newData.selectedDateMonthEntity = monthNames[monthIndex];
      newData.selectedDateFullYearEntity = year;
      newData.selectedDateYearEntity = year.slice(-1);
    }

    if (newData.domicileSelectedCity && newData.domicileSelectedState) {
      newData.domicileCombined = `${newData.domicileSelectedCity}, ${newData.domicileSelectedState}`;
    }


    if (prev.investorType === 'jointSpouse' &&
        prev.subscriberName1 && prev.subscriberName2) {
      newData.jointSubscriber = `${prev.subscriberName1} and ${prev.subscriberName2} as joint tenants`;
    }

    if (prev.subscriberName1) {
      newData.subscriberName1Colon = `: ${prev.subscriberName1}`;
    } else {
      newData.subscriberName1Colon = "";
    }

    if (prev.subscriberName2) {
      newData.subscriberName2Colon = `: ${prev.subscriberName2}`;
    } else {
      newData.subscriberName2Colon = "";
    }

    // 🖋️ Signature Touched flags for validation
    if (name === "signature") {
      newData.signatureTouched = true;
    }

    if (name === "signatureEntity" || name === "signatureEntityInd") {
      newData.signatureEntityTouched = true;
    }

    // 📦 Auto-build address3 from city, state, and zip
    const { city = "", state = "", zip = "" } = newData;
    newData.address3 = `${city}${city && state ? ", " : ""}${state}${state && zip ? " " : ""}${zip}`.trim();

    return newData;
  });
};

export default handleChange;



export const useFormState = () => {
    return useState({
        fundName: "",
        subscriberName: "",
        subscriberName1: "",
        subscriberName2: "",
        authorizedName: "",
        authorizedNameEntity: "",
        title: "",
        titleEntity: "",
        selectedDate: "",
        selectedDateEntity: "",
        amount: "",
        formattedAmount: "",
        address: "",
        address2: "",
        address3: "",
        city: "",
        state: "",
        zip: "",
        attention: "",
        email: "",
        phone: "",
        otherAddress: "",
        otherAddress2: "",
        otherCity: "",
        otherState: "",
        otherZip: "",
        otherAttention: "",
        otherPhone: "",
        otherEmail: "",
        rows: [
          {
            contactName: "",
            contactPhone: "",
            contactEmail: "",
            contactAddress1: "",
            contactAddress2: "",
            contactAddress3: "",
            contactNotices: "",
            contactCorrespondence: "",
            contactTaxInfo: "",
            contactLegalDocs: ""
          }
        ],
        accreditedInvestorStatus: {
            netWorth: "",
            income: "",
            executive: "",
            securitiesLicense: "",
            knowledgeableEmployee: "",
            familyClient: "",
        },
        investmentAdvisers: {
            netWorth2: "",
            highCommit: "",
            qualPurchaser: "",
        },
        qualifiedPurchaser: {
            ownsFiveMil: "",
        },
        miscMatters: {
            subject4975: "", 
            iraDecisionSPV: "",
            nonUsMemberStatus: "",
            representType: "",
            jointTenancyType: "",
            grantorTrustSettlor1: "",
            grantorTrustSettlor2: "",
            selfRetirementInd: "",
        },
        entityAccreditedInvestor: {
            bankLoan: "",
            brokerDealer: "",
            registeredAdviser: "",
            exemptAdviser: "",
            insuranceCompany: "",
            investmentCompany: "",
            businessDevelopment: "",
            smallBusiness: "",
            statePlan: "",
            erisaFiduciary: "",
            erisaAssets: "",
            erisaSelfDirected: "",
            privateBusinessDevCompany: "",
            corporationLLC: "",
            nonProfitOrg: "",
            businessTrust: "",
            trust5000000: "",
            entity5000000: "",
            familyOfficeEnt: "",
            familyClientEnt: "",
            entityAllAccredited: "",
        },
        entityDisqualifyingEvent: "",
        entityInvestmentCompany: {
            ivestOrBusiness: "",
            investmentCompany: "",
            lessThanTenPercent: "",
            notFormedForFund: "",
            lessThanFortyPercentAssets: "",
            priorInvestments: "",
            governingDocuments: "",
            collectiveInvestment: "",
            beneficialOwners: "",
        },
        entityInvestmentDetails: {
            investmentCompanyEntity: "",
            businessDevelopEntity: "",
            qualifiedClientNetWorth: "",
            lookThroughEntityOwnerNetWorth: "",
            lookThroughIndirectOwnerNetWorth: "",
        },
        entityInvestmentAdvisers: {
            entityInvests25M: "",
            trustQualifiedPurchaser: "",
            familyCompany: "",
            companyQualifiedPurchaser: "",
            qualifiedInstitutionalBuyer: "",
            notQualifiedPurchaser: "",
        },
        entityMiscMatters: {
            notBenefitPlanInvestor: "",
            isRetirementPlan: "",
            iraOwnerDecision: "",
            hasPlanAssets: "",
            isEntityetc: "",
            maxBenefitPlanPercentage: "",
            taxExemptMember: "",
            nonUSMember: "",
            corporation: "",
            generalPartnership: "",
            limitedPartnership: "",
            limitedLiabilityCompany: "",
            governmentEntity: "",
            trustType: "",
            otherEntity: "",
            jurisdiction: "",
            domicile: "",
            orgType: "",
            governmentPlans: "",
            foiaSubject: "",
        },
        entityMiscMattersFund: {
            notBenefitPlanInvestor: "",
            isRetirementPlan: "",
            iraOwnerDecision: "",
            hasPlanAssets: "",
            isEntityetc: "",
            maxBenefitPlanPercentage: "",
            govPlan: "",
            taxExemptPartner: "",
            nonUsPartner: "",
            erisaPartner: "",
            corporation: "",
            generalPartnership: "",
            limitedPartnership: "",
            limitedLiabilityCompany: "",
            governmentEntity: "",
            trustName: "",
            trustType: "",
            otherEntity: "",
            jurisdiction: "",
            domicile: "",
            orgType: "",
            governmentPlans: "",
            foiaSubject: "",
        },
        exceptedInvestmentCompany: "",
        country: "",
        signature: "",
        signatureEntity: "",
        signatureEntityInd: "",
        signatureSpouse: "",
        submissionMessage: "",
        disqualifyingEvent: "",
        selectedCountry: "",
        domicileSelectedCity: "",
        domicileSelectedState: "",
        formAgreementChecked: "",
        entityFormAgreementChecked: "",
        bankName: '',
        bankLocation: '',
        abaRoutingNumber: '',
        swiftCode: '',
        accountName: '',
        accountNumber: '',
        reference: '',
        entityBankName: '',
        entityBankLocation: '',
        entityAbaRoutingNumber: '',
        entitySwiftCode: '',
        entityAccountName: '',
        entityAccountNumber: '',
        entityReference: ''
      });
};
